import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DokterLayout from '../DokterLayout';
import axiosInstance from '../../../api/axios';

export default function Diagnosis() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  // State modal detail pasien
  const [selectedPatientGroup, setSelectedPatientGroup] = useState(null); 
  const [activeRm, setActiveRm] = useState(null); // RM yang sedang dipilih/diedit
  
  // Tab Mobile Default: 'history' (Riwayat Kunjungan)
  const [activeMobileTab, setActiveMobileTab] = useState('history'); 

  const [editingDiag, setEditingDiag] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [savingDiag, setSavingDiag] = useState(false);
  
  const navigate = useNavigate();

  const emptyResepItem = () => ({
    nama_obat: '', dosis: '', satuan: 'Tablet',
    frekuensi: '3x sehari', waktu: 'Sesudah makan', durasi: '', catatan: '',
  });

  useEffect(() => {
    fetchAllRecords();
  }, []);

  const fetchAllRecords = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/dokter/rekam-medis?per_page=500');
      const body = res.data;
      setRecords(body.data ?? body ?? []);
    } catch (err) {
      console.error('Error fetching rekam medis:', err);
    } finally {
      setLoading(false);
    }
  };

  // 🕒 Format Tanggal Saja
  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' }) : '-';

  // 🕒 Format Tanggal + Jam Input Medis
  const fmtDateTime = (d) => {
    if (!d) return '-';
    const dateObj = new Date(d);
    const dateStr = dateObj.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
    const timeStr = dateObj.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }).replace('.', ':');
    return `${dateStr} · ${timeStr} WIB`;
  };

  // 🕒 Format Jam Saja
  const fmtTime = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }).replace('.', ':') + ' WIB';
  };

  /* Helper Format ID Pasien / Kode Rekam Medis (Disamakan dengan Admin) */
  const formatPasienId = (id, kode) => {
    if (kode) return kode;
    if (!id) return 'PSN-0000';
    return `PSN-${String(id).padStart(4, '0')}`;
  };

  // Helper untuk mendapatkan nama pasien yang bersih
  const getPatientName = (pasienObj) => {
    if (!pasienObj) return 'Pasien Tanpa Nama';
    if (typeof pasienObj.nama === 'string' && pasienObj.nama.trim() !== '') {
      return pasienObj.nama;
    }
    if (typeof pasienObj.name === 'string' && pasienObj.name.trim() !== '') {
      return pasienObj.name;
    }
    return 'Pasien Tanpa Nama';
  };

  // Grouping Data Pasien
  const groupedPatients = useMemo(() => {
    const map = new Map();

    records.forEach((rm) => {
      const pId = rm.pasien_id || rm.pasien?.id || `rm-${rm.id}`;
      const visitDate = rm.created_at || rm.tanggal_kunjungan;
      
      if (!map.has(pId)) {
        map.set(pId, {
          pasien: rm.pasien || null,
          patientName: getPatientName(rm.pasien),
          pasienIdFormatted: formatPasienId(
            rm.pasien?.id || rm.pasien_id, 
            rm.pasien?.kode_pasien || rm.pasien?.no_rekam_medis
          ),
          records: [],
          lastVisit: visitDate,
        });
      }
      
      const group = map.get(pId);
      group.records.push(rm);

      if (new Date(visitDate) > new Date(group.lastVisit)) {
        group.lastVisit = visitDate;
      }
    });

    map.forEach((group) => {
      group.records.sort((a, b) => new Date(b.created_at || b.tanggal_kunjungan) - new Date(a.created_at || a.tanggal_kunjungan));
    });

    return Array.from(map.values());
  }, [records]);

  // Search Filter (Diagnosis Dihilangkan)
  const filteredPatients = groupedPatients.filter((group) => {
    const q = search.toLowerCase();
    if (!q) return true;
    
    const namaPasien = group.patientName.toLowerCase();
    const idPasien = group.pasienIdFormatted.toLowerCase();
    const hasMatchingComplaint = group.records.some(
      (r) => r.keluhan_utama?.toLowerCase().includes(q)
    );

    return namaPasien.includes(q) || idPasien.includes(q) || hasMatchingComplaint;
  });

  const parseResep = (str) => {
    try { return typeof str === 'string' ? JSON.parse(str) : (str ?? []); }
    catch { return []; }
  };

  const buildEditForm = (rm) => {
    const resepParsed = parseResep(rm.resep);
    return {
      keluhan_utama: rm.keluhan_utama || '',
      anamnesis: rm.anamnesis || '',
      pemeriksaan_fisik: rm.pemeriksaan_fisik || '',
      tindakan: rm.tindakan || '',
      catatan_dokter: rm.catatan_dokter || '',
      resep: resepParsed.length ? resepParsed : [emptyResepItem()],
    };
  };

  const handleOpenDetail = (group) => {
    setSelectedPatientGroup(group);
    setActiveRm(group.records[0]);
    setActiveMobileTab('history'); 
    setEditingDiag(false);
    setEditForm(null);
  };

  const startEditing = () => {
    if (!activeRm) return;
    setEditForm(buildEditForm(activeRm));
    setEditingDiag(true);
  };

  const cancelEditing = () => {
    setEditingDiag(false);
    setEditForm(null);
  };

  const upEditResep = (idx, field, val) => {
    setEditForm((f) => ({
      ...f,
      resep: f.resep.map((r, i) => (i === idx ? { ...r, [field]: val } : r)),
    }));
  };

  const handleSaveDiagnosis = async () => {
    if (!activeRm?.id || !editForm?.keluhan_utama?.trim()) {
      alert('Keluhan utama wajib diisi.');
      return;
    }
    setSavingDiag(true);
    try {
      const res = await axiosInstance.put(`/dokter/rekam-medis/${activeRm.id}`, {
        keluhan_utama: editForm.keluhan_utama.trim(),
        anamnesis: editForm.anamnesis.trim() || null,
        pemeriksaan_fisik: editForm.pemeriksaan_fisik.trim() || null,
        tindakan: editForm.tindakan.trim() || null,
        catatan_dokter: editForm.catatan_dokter.trim() || null,
        resep: editForm.resep.filter((r) => r.nama_obat?.trim()),
      });

      const updatedData = res.data?.data || res.data;

      setRecords((prev) =>
        prev.map((r) => (r.id === activeRm.id ? { ...r, ...updatedData } : r))
      );

      setActiveRm((prev) => ({ ...prev, ...updatedData }));
      setSelectedPatientGroup((prev) => ({
        ...prev,
        records: prev.records.map((r) => (r.id === activeRm.id ? { ...r, ...updatedData } : r)),
      }));

      setEditingDiag(false);
      setEditForm(null);
    } catch (err) {
      console.error(err);
      alert('Gagal menyimpan rekam medis.');
    } finally {
      setSavingDiag(false);
    }
  };

  return (
    <DokterLayout title="Riwayat Pasien">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
        .dx * { font-family: 'DM Sans', sans-serif; box-sizing: border-box; }

        .dx-header { 
          display: flex; 
          align-items: center; 
          justify-content: space-between; 
          gap: 16px; 
          margin-bottom: 22px; 
          flex-wrap: wrap; 
        }
        .dx-title { font-size: 22px; font-weight: 700; color: #111827; margin: 0; }
        .dx-sub   { font-size: 13px; color: #6B7280; margin: 2px 0 0; }

        .dx-search {
          display: flex; align-items: center; gap: 8px; background: #fff;
          border: 1.5px solid #E5E7EB; border-radius: 10px; padding: 9px 14px; 
          width: 100%; max-width: 320px;
        }
        .dx-search input { border: none; outline: none; font-size: 14px; width: 100%; background: transparent; }

        .dx-card { background: #fff; border: 1.5px solid #E5E7EB; border-radius: 14px; overflow: hidden; }
        
        /* Table Default (Desktop) */
        .dx-table-wrapper { width: 100%; overflow-x: auto; }
        .dx-table { width: 100%; border-collapse: collapse; }
        .dx-table thead tr { background: #F9FAFB; border-bottom: 1.5px solid #E5E7EB; }
        .dx-table th { padding: 12px 16px; text-align: left; font-size: 11px; font-weight: 700; color: #6B7280; text-transform: uppercase; }
        .dx-table tbody tr { border-bottom: 1px solid #F3F4F6; cursor: pointer; transition: background .12s; }
        .dx-table tbody tr:hover { background: #F9FAFB; }
        .dx-table td { padding: 14px 16px; font-size: 14px; color: #374151; vertical-align: middle; }

        .id-badge {
          display: inline-block;
          padding: 3px 8px;
          border-radius: 6px;
          background-color: #F3F4F6;
          color: #374151;
          font-family: monospace;
          font-size: 12px;
          font-weight: 700;
          border: 1px solid #E5E7EB;
        }

        /* Mobile Cards */
        .dx-mobile-list { display: none; padding: 12px; }
        .patient-card-mobile {
          background: #fff; border: 1px solid #E5E7EB; border-radius: 10px;
          padding: 14px; margin-bottom: 10px; cursor: pointer; transition: all .15s;
        }
        .patient-card-mobile:active { background: #F9FAFB; }
        .patient-card-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px; }

        .patient-name { font-weight: 700; color: #111827; }
        .patient-meta { font-size: 12px; color: #9CA3AF; margin-top: 2px; }

        .badge-count {
          display: inline-block; background: #EEF2FF; color: #4338CA;
          font-weight: 700; padding: 3px 10px; border-radius: 20px; font-size: 12px;
        }

        .badge-time {
          display: inline-flex; align-items: center; gap: 4px; background: #F3F4F6; color: #4B5563;
          font-weight: 600; padding: 3px 8px; border-radius: 6px; font-size: 11px; margin-top: 4px;
        }

        .btn-detail {
          padding: 6px 14px; background: #4F46E5; border: none;
          border-radius: 7px; color: #fff; font-size: 12px; font-weight: 700; cursor: pointer;
        }
        .btn-detail:hover { background: #4338CA; }

        .dx-empty { text-align: center; padding: 64px 20px; color: #9CA3AF; }

        /* Modal Styles */
        .modal-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,.45);
          z-index: 200; display: flex; align-items: center; justify-content: center; padding: 16px;
        }
        .modal-box {
          background: #fff; border-radius: 18px; width: 100%; max-width: 850px;
          max-height: 90vh; overflow: hidden; box-shadow: 0 24px 64px rgba(0,0,0,.18);
          display: flex; flex-direction: column;
        }
        .modal-head {
          display: flex; align-items: center; justify-content: space-between;
          padding: 16px 20px; border-bottom: 1px solid #E5E7EB; background: #F9FAFB;
        }
        .modal-body-layout { display: flex; flex: 1; overflow: hidden; }

        /* Mobile Modal Tabs */
        .mobile-tab-nav { display: none; background: #F3F4F6; border-bottom: 1px solid #E5E7EB; }
        .mobile-tab-btn {
          flex: 1; padding: 10px; text-align: center; font-size: 13px; font-weight: 600;
          color: #6B7280; border: none; background: transparent; cursor: pointer;
        }
        .mobile-tab-btn.active { color: #4F46E5; background: #fff; border-bottom: 2px solid #4F46E5; }

        /* Sidebar Timeline Kunjungan */
        .history-sidebar {
          width: 270px; background: #F9FAFB; border-right: 1px solid #E5E7EB;
          overflow-y: auto; padding: 16px; flex-shrink: 0;
        }
        .history-title { font-size: 11px; font-weight: 700; color: #9CA3AF; text-transform: uppercase; margin-bottom: 12px; }
        .history-item {
          padding: 10px 12px; border-radius: 8px; border: 1.5px solid #E5E7EB;
          background: #fff; margin-bottom: 8px; cursor: pointer; transition: all .15s;
        }
        .history-item.active { border-color: #6366F1; background: #EEF2FF; }
        .history-date { font-weight: 700; font-size: 13px; color: #111827; }
        .history-time { font-size: 11px; color: #6B7280; margin-top: 2px; }
        .history-complaint { font-size: 12px; color: #4B5563; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 4px; }

        /* Detail Content */
        .detail-content { flex: 1; overflow-y: auto; padding: 24px; }
        .modal-section { margin-bottom: 18px; }
        .modal-section h5 { font-size: 11px; font-weight: 700; color: #9CA3AF; text-transform: uppercase; margin: 0 0 6px; }
        .modal-section p { font-size: 14px; color: #374151; margin: 0; line-height: 1.5; white-space: pre-wrap; }

        .resep-card { background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 8px; padding: 10px 12px; margin-bottom: 6px; }
        .diag-edit-area { width: 100%; min-height: 70px; padding: 8px 12px; border: 1.5px solid #E5E7EB; border-radius: 8px; font-size: 13px; }
        .modal-input { width: 100%; padding: 8px; border: 1.5px solid #E5E7EB; border-radius: 6px; font-size: 13px; }

        .edit-form-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 16px; }

        @media (max-width: 768px) {
          .dx-header { flex-direction: column; align-items: stretch; gap: 12px; }
          .dx-search { max-width: 100%; }

          .dx-table-wrapper { display: none; }
          .dx-mobile-list { display: block; }

          .modal-overlay { padding: 10px; }
          .modal-box { max-height: 95vh; border-radius: 14px; }
          .modal-head { padding: 14px 16px; }
          .modal-head h3 { font-size: 16px !important; }

          .mobile-tab-nav { display: flex; }
          .modal-body-layout { flex-direction: column; }

          .history-sidebar {
            width: 100%;
            border-right: none;
            border-bottom: 1px solid #E5E7EB;
            display: ${activeMobileTab === 'history' ? 'block' : 'none'};
            max-height: 100%;
          }

          .detail-content {
            padding: 16px;
            display: ${activeMobileTab === 'detail' ? 'block' : 'none'};
          }

          .detail-header-action {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 10px;
          }

          .edit-form-actions { flex-direction: column-reverse; }
          .edit-form-actions button { width: 100%; padding: 10px !important; }
        }
      `}</style>

      <div className="dx">
        <div className="dx-header">
          <div>
            <h2 className="dx-title">Riwayat Berobat Pasien</h2>
            <p className="dx-sub">Daftar pasien beserta seluruh riwayat kunjungannya</p>
          </div>
          <div className="dx-search">
            <span>🔍</span>
            <input
              placeholder="Cari ID Pasien, nama, atau keluhan…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="dx-card">
          {loading ? (
            <div className="dx-empty">⏳ Memuat data riwayat pasien…</div>
          ) : filteredPatients.length === 0 ? (
            <div className="dx-empty">📋 Tidak ada data pasien ditemukan.</div>
          ) : (
            <>
              {/* DESKTOP TABLE VIEW */}
              <div className="dx-table-wrapper">
                <table className="dx-table">
                  <thead>
                    <tr>
                      <th style={{ width: '130px' }}>ID Pasien</th>
                      <th>Pasien</th>
                      <th>Terakhir Berobat</th>
                      <th>Total Berobat</th>
                      <th style={{ textAlign: 'right' }}>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPatients.map((group, idx) => (
                      <tr key={idx} onClick={() => handleOpenDetail(group)}>
                        <td>
                          <span className="id-badge">{group.pasienIdFormatted}</span>
                        </td>
                        <td>
                          <div className="patient-name">{group.patientName}</div>
                          <div className="patient-meta">{group.pasien?.jenis_kelamin ?? 'Jenis kelamin -'}</div>
                        </td>
                        <td>
                          <div>{fmtDate(group.lastVisit)}</div>
                          <div className="badge-time">🕒 {fmtTime(group.lastVisit)}</div>
                        </td>
                        <td>
                          <span className="badge-count">{group.records.length} Kali Berobat</span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <button className="btn-detail">Lihat Riwayat →</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* MOBILE CARD VIEW */}
              <div className="dx-mobile-list">
                {filteredPatients.map((group, idx) => (
                  <div key={idx} className="patient-card-mobile" onClick={() => handleOpenDetail(group)}>
                    <div className="patient-card-header">
                      <div>
                        <div style={{ marginBottom: 4 }}>
                          <span className="id-badge">{group.pasienIdFormatted}</span>
                        </div>
                        <div className="patient-name">{group.patientName}</div>
                        <div className="patient-meta">{group.pasien?.jenis_kelamin ?? 'Jenis kelamin -'}</div>
                      </div>
                      <span className="badge-count">{group.records.length}x Kunjungan</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, paddingTop: 10, borderTop: '1px solid #F3F4F6' }}>
                      <span style={{ fontSize: 12, color: '#6B7280' }}>
                        Terakhir: <strong>{fmtDateTime(group.lastVisit)}</strong>
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#4F46E5' }}>Detail →</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── MODAL RIWAYAT PASIEN ── */}
      {selectedPatientGroup && (
        <div className="modal-overlay" onClick={() => setSelectedPatientGroup(null)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            
            {/* Header Modal */}
            <div className="modal-head">
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <h3 style={{ margin: 0, fontSize: 18, color: '#111827' }}>
                    {selectedPatientGroup.patientName}
                  </h3>
                  <span className="id-badge">{selectedPatientGroup.pasienIdFormatted}</span>
                </div>
                <span style={{ fontSize: 12, color: '#6B7280', display: 'block', marginTop: 2 }}>
                  {selectedPatientGroup.pasien?.jenis_kelamin ?? '-'} · Terakhir Berobat: {fmtDateTime(selectedPatientGroup.lastVisit)}
                </span>
              </div>
              <button
                style={{ background: '#F3F4F6', border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontWeight: 600 }}
                onClick={() => setSelectedPatientGroup(null)}
              >
                ✕ Tutup
              </button>
            </div>

            {/* Mobile Tab Navigation */}
            <div className="mobile-tab-nav">
              <button
                className={`mobile-tab-btn ${activeMobileTab === 'history' ? 'active' : ''}`}
                onClick={() => setActiveMobileTab('history')}
              >
                📅 Riwayat ({selectedPatientGroup.records.length})
              </button>
              <button
                className={`mobile-tab-btn ${activeMobileTab === 'detail' ? 'active' : ''}`}
                onClick={() => setActiveMobileTab('detail')}
              >
                📄 Detail Kunjungan
              </button>
            </div>

            <div className="modal-body-layout">
              {/* Sidebar Timeline Kunjungan */}
              <div className="history-sidebar">
                <div className="history-title">Riwayat Kunjungan</div>
                {selectedPatientGroup.records.map((rm) => {
                  const timestamp = rm.created_at || rm.tanggal_kunjungan;
                  return (
                    <div
                      key={rm.id}
                      className={`history-item ${activeRm?.id === rm.id ? 'active' : ''}`}
                      onClick={() => {
                        setActiveRm(rm);
                        setEditingDiag(false);
                        setActiveMobileTab('detail');
                      }}
                    >
                      <div className="history-date">📅 {fmtDate(timestamp)}</div>
                      <div className="history-time">🕒 Input: {fmtTime(timestamp)}</div>
                      <div className="history-complaint">
                        {rm.keluhan_utama || 'Tanpa Catatan Keluhan'}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Detail Rekam Medis yang Dipilih */}
              <div className="detail-content">
                {activeRm ? (
                  <>
                    <div className="detail-header-action" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
                      <div>
                        <h4 style={{ margin: 0, color: '#111827' }}>
                          Kunjungan ({fmtDate(activeRm.created_at || activeRm.tanggal_kunjungan)})
                        </h4>
                        <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                          🕒 Waktu Input Medis: <strong>{fmtDateTime(activeRm.created_at || activeRm.tanggal_kunjungan)}</strong>
                        </div>
                      </div>
                      {!editingDiag && (
                        <button
                          style={{ padding: '6px 14px', background: '#F0FDF4', border: '1px solid #BBF7D0', color: '#16A34A', borderRadius: 6, fontWeight: 700, cursor: 'pointer' }}
                          onClick={startEditing}
                        >
                          ✏️ Edit Rekam Medis Ini
                        </button>
                      )}
                    </div>

                    {editingDiag && editForm ? (
                      /* MODE EDIT REKAM MEDIS */
                      <div>
                        <div className="modal-section">
                          <h5>Keluhan Utama *</h5>
                          <textarea className="diag-edit-area" value={editForm.keluhan_utama} onChange={(e) => setEditForm({ ...editForm, keluhan_utama: e.target.value })} />
                        </div>
                        <div className="modal-section">
                          <h5>Anamnesis</h5>
                          <textarea className="diag-edit-area" value={editForm.anamnesis} onChange={(e) => setEditForm({ ...editForm, anamnesis: e.target.value })} />
                        </div>
                        <div className="modal-section">
                          <h5>Pemeriksaan Fisik</h5>
                          <textarea className="diag-edit-area" value={editForm.pemeriksaan_fisik} onChange={(e) => setEditForm({ ...editForm, pemeriksaan_fisik: e.target.value })} />
                        </div>
                        <div className="modal-section">
                          <h5>Tindakan Medis</h5>
                          <textarea className="diag-edit-area" value={editForm.tindakan} onChange={(e) => setEditForm({ ...editForm, tindakan: e.target.value })} />
                        </div>

                        <div className="modal-section">
                          <h5>Resep Obat</h5>
                          {editForm.resep.map((obat, i) => (
                            <div key={i} className="resep-card">
                              <input className="modal-input" placeholder="Nama obat" value={obat.nama_obat} onChange={(e) => upEditResep(i, 'nama_obat', e.target.value)} />
                              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                                <input className="modal-input" placeholder="Dosis" value={obat.dosis} onChange={(e) => upEditResep(i, 'dosis', e.target.value)} />
                                <input className="modal-input" placeholder="Frekuensi" value={obat.frekuensi} onChange={(e) => upEditResep(i, 'frekuensi', e.target.value)} />
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="modal-section">
                          <h5>Catatan Dokter</h5>
                          <textarea className="diag-edit-area" value={editForm.catatan_dokter} onChange={(e) => setEditForm({ ...editForm, catatan_dokter: e.target.value })} />
                        </div>

                        <div className="edit-form-actions">
                          <button style={{ padding: '8px 16px', background: '#F3F4F6', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }} onClick={cancelEditing}>Batal</button>
                          <button style={{ padding: '8px 16px', background: '#16A34A', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }} disabled={savingDiag} onClick={handleSaveDiagnosis}>
                            {savingDiag ? 'Menyimpan...' : 'Simpan Perubahan'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* MODE VIEW DETAIL REKAM MEDIS */
                      <div>
                        <div className="modal-section">
                          <h5>Keluhan Utama</h5>
                          <p>{activeRm.keluhan_utama || '-'}</p>
                        </div>

                        {activeRm.anamnesis && (
                          <div className="modal-section">
                            <h5>Anamnesis</h5>
                            <p>{activeRm.anamnesis}</p>
                          </div>
                        )}

                        {activeRm.pemeriksaan_fisik && (
                          <div className="modal-section">
                            <h5>Pemeriksaan Fisik</h5>
                            <p>{activeRm.pemeriksaan_fisik}</p>
                          </div>
                        )}

                        {activeRm.tindakan && (
                          <div className="modal-section">
                            <h5>Tindakan Medis</h5>
                            <p>{activeRm.tindakan}</p>
                          </div>
                        )}

                        <div className="modal-section">
                          <h5>Resep Obat</h5>
                          {parseResep(activeRm.resep).length > 0 ? (
                            parseResep(activeRm.resep).map((o, i) => (
                              <div key={i} className="resep-card">
                                <strong>{o.nama_obat}</strong> — {o.dosis} {o.satuan} ({o.frekuensi} · {o.waktu})
                              </div>
                            ))
                          ) : (
                            <p>-</p>
                          )}
                        </div>

                        {activeRm.catatan_dokter && (
                          <div className="modal-section">
                            <h5>Catatan Dokter</h5>
                            <p>{activeRm.catatan_dokter}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div>Pilih riwayat kunjungan di tab riwayat.</div>
                )}
              </div>
            </div>

          </div>
        </div>
      )}
    </DokterLayout>
  );
}